/**
 * The document-library detail-pane layout — shared by the global Document
 * Library (DocumentLibraryContent) and the client-workspace Library tab
 * (ClientLibraryTab). Extracted from DocumentLibraryContent so both surfaces
 * open the SAME panel with identical resize/animation behavior instead of
 * duplicating ~240 lines of split-pane choreography.
 *
 * Division of ownership: the HOST owns `selectedDocumentId` (it drives the
 * row selection highlight, `?doc=` deep-links, and tab-transition closes);
 * this component owns only the panel's width/animation state and the
 * ResizeObserver → `panelResizeTick` wiring. The list content is supplied as
 * a render prop so it can receive `panelResizeTick` and forward it to
 * DocumentList, whose virtualizer must re-measure when the panel resizes.
 */
import {
    type PointerEvent as ReactPointerEvent,
    type ReactNode,
    useCallback,
    useEffect,
    useReducer,
    useRef,
    useState,
} from "react";

import { useLocalStorage } from "@/hooks/useLocalStorage";
import { cn } from "@/lib/utils";

import { DocumentDetailPanel } from "./DocumentDetailPanel";

// Detail panel width is persisted as a percentage of the container. The key
// is shared across surfaces so the user's chosen width follows them between
// the global library and a client workspace.
const DETAIL_PANEL_STORAGE_KEY = "library-detail-panel-size";
const DEFAULT_DETAIL_PANEL_PERCENT = 33;
const MIN_DETAIL_PANEL_PERCENT = 25;
const MAX_DETAIL_PANEL_PERCENT = 60;
const PANEL_ANIMATION_MS = 280;
const PANEL_TRANSITION = `width ${PANEL_ANIMATION_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`;

/** The effective open id and its close handler, resolved by the layout:
 * `id` is immediate on mobile and tween-preserved on desktop. */
export interface DetailPanelRenderArgs {
    open: boolean;
    onClose: () => void;
    id: number | null;
    isMobile: boolean;
}

export interface DocumentDetailLayoutProps {
    /** Mobile renders the panel as a Sheet overlay (the panel handles that
     * internally); desktop renders a resizable sticky split-pane. Same
     * breakpoint source as the list surfaces. */
    isMobile: boolean;
    /** The open document (null = closed). Owned by the host surface. */
    selectedDocumentId: number | null;
    onClose: () => void;
    /** The list content. Receives `panelResizeTick`, which bumps when the
     * open/close tween settles or a drag resizes the panel — forward it to
     * DocumentList so its virtualizer re-measures against the new width. */
    children: (panelResizeTick: number) => ReactNode;
    /** Overrides the mounted detail panel. Defaults to DocumentDetailPanel so
     * the global library and the client workspace stay identical; the org
     * Templates surface passes one that mounts TemplateDetailPanel. */
    renderPanel?: (args: DetailPanelRenderArgs) => ReactNode;
}

export const DocumentDetailLayout = ({
    isMobile,
    selectedDocumentId,
    onClose,
    children,
    renderPanel,
}: DocumentDetailLayoutProps) => {
    const [persistedDetailSize, setPersistedDetailSize] =
        useLocalStorage<number>(
            DETAIL_PANEL_STORAGE_KEY,
            DEFAULT_DETAIL_PANEL_PERCENT
        );
    // Live size during drag — kept separate so localStorage isn't written
    // on every pointermove tick; we persist once on pointerup. Clamp on
    // read so a stale stored value (e.g. after MIN/MAX changes) can't
    // render the panel out of range.
    const [detailSize, setDetailSize] = useState(() =>
        Math.max(
            MIN_DETAIL_PANEL_PERCENT,
            Math.min(MAX_DETAIL_PANEL_PERCENT, persistedDetailSize)
        )
    );

    const isDetailOpen = selectedDocumentId !== null;

    // Keep detail content mounted through the close tween so the panel
    // doesn't go blank while collapsing.
    const [displayedDocumentId, setDisplayedDocumentId] = useState<
        number | null
    >(selectedDocumentId);
    if (
        selectedDocumentId !== null &&
        selectedDocumentId !== displayedDocumentId
    ) {
        setDisplayedDocumentId(selectedDocumentId);
    }
    useEffect(() => {
        if (selectedDocumentId !== null) return;
        const timer = setTimeout(
            () => setDisplayedDocumentId(null),
            PANEL_ANIMATION_MS
        );
        return () => clearTimeout(timer);
    }, [selectedDocumentId]);

    const [isDragging, setIsDragging] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const [panelResizeTick, bumpPanelResizeTick] = useReducer(
        (c: number) => c + 1,
        0
    );

    // Skip ResizeObserver-driven virtualization recomputes during the open/close
    // tween — they compete with the width transition and cause visible stutter.
    // Drag is exempt: continuous bumps are the user's intent.
    const isPanelAnimatingRef = useRef(false);
    const isDraggingRef = useRef(false);

    const prevDetailOpenRef = useRef<boolean | null>(null);
    useEffect(() => {
        if (prevDetailOpenRef.current === null) {
            prevDetailOpenRef.current = isDetailOpen;
            return;
        }
        if (prevDetailOpenRef.current !== isDetailOpen) {
            prevDetailOpenRef.current = isDetailOpen;
            isPanelAnimatingRef.current = true;
        }
    }, [isDetailOpen]);

    const listPanelContentRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const node = listPanelContentRef.current;
        if (!node) return;
        const ro = new ResizeObserver(() => {
            if (isPanelAnimatingRef.current && !isDraggingRef.current) return;
            bumpPanelResizeTick();
        });
        ro.observe(node);
        return () => ro.disconnect();
    }, []);

    const handleResizePointerDown = useCallback(
        (e: ReactPointerEvent<HTMLDivElement>) => {
            if (!isDetailOpen) return;
            e.preventDefault();
            const container = containerRef.current;
            if (!container) return;
            const rect = container.getBoundingClientRect();
            if (rect.width <= 0) return;

            setIsDragging(true);
            isDraggingRef.current = true;
            // The user is taking over from any in-flight open/close tween.
            // Clear the animating flag so the ResizeObserver guard doesn't
            // suppress autosize after the drag releases (the drag swaps
            // transition to "none", so onTransitionEnd never fires).
            isPanelAnimatingRef.current = false;
            let lastClamped = detailSize;

            const onMove = (ev: PointerEvent) => {
                const detailPx = rect.right - ev.clientX;
                const pct = (detailPx / rect.width) * 100;
                const clamped = Math.max(
                    MIN_DETAIL_PANEL_PERCENT,
                    Math.min(MAX_DETAIL_PANEL_PERCENT, pct)
                );
                if (clamped === lastClamped) return;
                lastClamped = clamped;
                setDetailSize(clamped);
            };
            const onUp = () => {
                setIsDragging(false);
                isDraggingRef.current = false;
                setPersistedDetailSize(lastClamped);
                window.removeEventListener("pointermove", onMove);
                window.removeEventListener("pointerup", onUp);
                window.removeEventListener("pointercancel", onUp);
            };
            window.addEventListener("pointermove", onMove);
            window.addEventListener("pointerup", onUp);
            window.addEventListener("pointercancel", onUp);
        },
        [isDetailOpen, detailSize, setPersistedDetailSize]
    );

    const handleResetDetailPanelSize = useCallback(() => {
        setDetailSize(DEFAULT_DETAIL_PANEL_PERCENT);
        setPersistedDetailSize(DEFAULT_DETAIL_PANEL_PERCENT);
    }, [setPersistedDetailSize]);

    const renderDetailPanel =
        renderPanel ??
        ((args: DetailPanelRenderArgs) => (
            <DocumentDetailPanel
                open={args.open}
                onClose={args.onClose}
                documentId={args.id}
                isMobile={args.isMobile}
            />
        ));

    const detailPanel = renderDetailPanel({
        open: isDetailOpen,
        onClose,
        id: isMobile ? selectedDocumentId : displayedDocumentId,
        isMobile,
    });

    if (isMobile) {
        return (
            <div className="flex min-h-0">
                <div className="flex min-w-0 flex-1 flex-col pr-4">
                    {children(panelResizeTick)}
                </div>
                {detailPanel}
            </div>
        );
    }

    return (
        <div className="flex min-h-0">
            <div ref={containerRef} className="flex w-full items-start">
                <div
                    ref={listPanelContentRef}
                    className="flex min-w-0 flex-1 flex-col pr-4"
                >
                    {children(panelResizeTick)}
                </div>
                <div
                    role="separator"
                    aria-orientation="vertical"
                    aria-valuemin={MIN_DETAIL_PANEL_PERCENT}
                    aria-valuemax={MAX_DETAIL_PANEL_PERCENT}
                    aria-valuenow={Math.round(detailSize)}
                    title="Drag to resize • Double-click to reset"
                    onPointerDown={handleResizePointerDown}
                    onDoubleClick={handleResetDetailPanelSize}
                    // Sticky alongside the detail panel so the resizer
                    // stays visible/grabbable across the full scroll
                    // range (same max-h math as the panel) — but only
                    // while the panel shows. The bar stays in flow when
                    // hidden, so a permanent fixed height would force the
                    // split-pane row to full scrollport height and give
                    // hosts with chrome above the layout (the client
                    // workspace header + tabs) a phantom page scroll. It
                    // holds height through the close tween (it fades out
                    // in place) via displayedDocumentId, then releases.
                    className={cn(
                        "bg-border sticky top-0 flex w-px flex-shrink-0 cursor-col-resize items-center justify-center transition-opacity duration-200 after:absolute after:inset-y-0 after:left-1/2 after:w-1 after:-translate-x-1/2",
                        isDetailOpen || displayedDocumentId !== null
                            ? "h-[100cqh]"
                            : "h-0",
                        !isDetailOpen && "pointer-events-none opacity-0"
                    )}
                >
                    <div className="bg-border z-10 flex h-6 w-1 shrink-0 rounded-lg" />
                </div>
                <div
                    // Sticky against the HOST's nearest scrolling ancestor —
                    // the document library's FrontendPageContent scrollport,
                    // or the client workspace's library pane. 100cqh sizes
                    // the panel to that scrollport's content box, so it
                    // exactly fills the visible content area regardless of
                    // what chrome the host stacks above the layout. The host
                    // contract: the scroll container holding this layout must
                    // declare [container-type:size]. Without one, cqh falls
                    // back to viewport units, the panel overhangs the
                    // scrollport, and the page grows a second scrollbar on
                    // top of the panel's own — the client workspace shipped
                    // exactly that while these heights were viewport-derived
                    // (100dvh - --top-offset - 2rem), because its header and
                    // tabs ate part of the viewport the math assumed free.
                    className="bg-background sticky top-0 flex max-h-[100cqh] flex-shrink-0 flex-col overflow-y-auto"
                    style={{
                        width: isDetailOpen ? `${detailSize}%` : "0%",
                        transition: isDragging ? "none" : PANEL_TRANSITION,
                    }}
                    onTransitionEnd={(e) => {
                        if (
                            e.propertyName !== "width" ||
                            e.target !== e.currentTarget
                        )
                            return;
                        isPanelAnimatingRef.current = false;
                        bumpPanelResizeTick();
                    }}
                >
                    {detailPanel}
                </div>
            </div>
        </div>
    );
};
